import { useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Paragraph, QuillText, Tooltip, tokens } from '@treams/treams-ui';

const Bullet = styled(Flex)<{
	filled?: boolean;
	party: graphql.EvaluationSubmissionParty;
	enabled: boolean;
}>`
	width: 32px;
	height: 32px;
	border-radius: 16px;
	border: 1px solid #dae0e8;
	overflow: hidden;

	${(props) => (props.enabled ? `cursor: pointer` : `opacity: 0.25`)};

	${(props) =>
		props.filled &&
		`border: none; background: ${
			props.party === graphql.EvaluationSubmissionParty.Employee
				? props.theme.colors.employee
				: props.theme.colors.manager
		}`};
`;

const Dot = styled.div<{ filled: boolean }>`
	position: absolute;
	left: 12px;
	top: 12px;
	width: 8px;
	height: 8px;
	background: ${(props) => (props.filled ? 'rgba(0, 0, 0, 0.1)' : '#dae0e8')};
	border-radius: 8px;
`;

const Label = styled.div`
	color: ${tokens.text.secondary};
	text-overflow: ellipsis;
	overflow: hidden;
`;

const TooltipTitle = styled.div`
	border-top: 1px solid ${tokens.border.subtle};
	padding-top: 10px;
	margin: 0px -20px;
	margin-top: 10px !important;
	margin-bottom: -5px;
	text-align: center;
`;

function CompetencyInputLevel(props: {
	index: number;
	name: string;
	level: graphql.CompetencyLevelOption;
	party: graphql.EvaluationSubmissionParty;
	suffix?: string;
	initialValue?: number;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();

	const [, { value }, { setValue }] = useField(props.name + (props.suffix ?? '.competency'));

	const actualValue = value ?? props.initialValue;

	return (
		<Box
			mx={1}
			style={{ position: 'relative' }}
			onClick={() => props.level.enabled && setValue(props.index)}
		>
			<Tooltip
				enabled={props.level.enabled}
				content={
					<Box p={4}>
						<Paragraph>
							<QuillText
								value={localize(props.level.criteria ?? [])?.text}
								placeholder={t('evaluation.submission.competency.levels.noDescription')}
							/>
						</Paragraph>
						<TooltipTitle>{localize(props.level.level.title)?.text}</TooltipTitle>
					</Box>
				}
			>
				<Bullet
					filled={actualValue === props.index}
					party={props.party}
					enabled={props.level.enabled}
				>
					<Dot filled={actualValue === props.index} />
				</Bullet>
			</Tooltip>
		</Box>
	);
}

export default function CompetencyLevelsInput(props: {
	name: string;
	initialValue?: number;
	competency: graphql.Competency;
	party: graphql.EvaluationSubmissionParty;
	suffix?: string;
}) {
	const { localize } = useLocalize();

	const levels = props.competency.levels;
	// Use enabled levels for the left and right legend text.
	const enabledLevels = levels.filter((level) => !!level.enabled);

	const bullets = levels
		.map((level, i) => [level, i] as [graphql.CompetencyLevelOption, number])
		.filter(([level]) => level.enabled)
		.map(([level, i]) => (
			<CompetencyInputLevel
				key={i}
				index={i}
				level={level}
				name={props.name}
				suffix={props.suffix}
				party={props.party}
				initialValue={props.initialValue}
			/>
		));

	return (
		<Flex alignItems="center" justifyContent="space-between">
			{enabledLevels.length > 0 && (
				<Box mr={2}>
					<Label>{localize(enabledLevels[0].level.title).text}</Label>
				</Box>
			)}
			{bullets}
			{enabledLevels.length > 0 && (
				<Box ml={2}>
					<Label>{localize(enabledLevels[enabledLevels.length - 1].level.title).text}</Label>
				</Box>
			)}
		</Flex>
	);
}
